import { FunctionComponent, memo, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ChannelContext, EventDetailsModalContext, EventDetailsTab } from '../../../../../context';
import { Event, EventStatus } from '../../../../../services';
import { eventStatusUpdate, updateCardEvent } from '../../../../../slices';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useEventEdit } from '../../../../hooks/useEventEdit';
import { useIsNarrowViewport } from '../../../../hooks/useIsNarrowViewport';
import { getCssVar, isEventPast } from '../../../../utils';
import { IconLabel, IconLabelHintDirection } from '../../../IconLabel';
import { LineClamp } from '../../../LineClamp';
import { PublishToggle } from '../../../PublishToggle';
import { ScheduleIcon } from '../../../ScheduleIcon';
import { eventDateTime, eventRegistrationDeadline } from '../../helpers';
import { EventActionButton } from '../EventActionButton';
import { EventActionsMenu } from '../EventActionsMenu';
import { EventLocation } from '../EventLocation';
import { EventParticipants } from '../EventParticipants';
import { EventShare } from '../EventShare';

import classNames from 'classnames';
import classes from './EventItemBody.module.scss';

interface EventItemBodyProps {
  event: Event;
  showDescription: boolean;
  showViewLink: boolean;
  onDeleteSuccess?: () => void;
  onSelectTab?: (tab: EventDetailsTab) => void;
}

export const EventItemBody: FunctionComponent<EventItemBodyProps> = memo(
  ({ event, showDescription, showViewLink, onDeleteSuccess, onSelectTab }) => {
    const { t } = useTranslation();

    const { t: ctxT } = useTranslation('', { keyPrefix: 'eventItem' });

    const dispatch = useAppDispatch();

    const { id: channelId } = useContext(ChannelContext).channel;

    const { setEventDetailsModalData } = useContext(EventDetailsModalContext);

    const isNarrowViewport = useIsNarrowViewport();

    const {
      id: eventId,
      descriptionHTML,
      status,
      startAt,
      endAt,
      scheduledAt,
      permissions,
    } = event;

    const { allowToEdit } = permissions;

    const isPublished = status === EventStatus.PUBLISHED;

    const isCancelled = status === EventStatus.CANCELLED;

    const isEditable = allowToEdit && !isCancelled && !isEventPast(startAt, endAt);

    const [isPublishLoading, setIsPublishLoading] = useState(false);

    const { eventEditModal, eventEditHandler } = useEventEdit({ event });

    const openEventDetails = useCallback(() => {
      setEventDetailsModalData({ eventId });
    }, [setEventDetailsModalData, eventId]);

    const onPublishToggle = useCallback(async () => {
      setIsPublishLoading(true);
      const status = isPublished ? EventStatus.UNPUBLISHED : EventStatus.PUBLISHED;

      try {
        await dispatch(eventStatusUpdate({ channelId, eventId, fields: { status } }));
        await dispatch(updateCardEvent({ channelId, eventId }));
      } catch {
        toast.error(t('common.error-message'));
      } finally {
        setIsPublishLoading(false);
      }
    }, [channelId, dispatch, eventId, isPublished, t]);

    return (
      <>
        {showDescription && (
          <LineClamp
            maxLines={2}
            htmlStringContent={descriptionHTML}
            contentClassName={classes['event-item-body__description']}
            readMoreClassName={classes['event-item-body__description']}
            readMore={'...'}
            onClick={openEventDetails}
          />
        )}

        <div className={classes['event-item-body__info']}>
          <div className={classes['event-item-body__info-row']}>
            <IconLabel iconId={'clock'} iconSize={16} nonClickable singleColor />
            {eventDateTime(event)}
          </div>

          <EventLocation event={event} />

          <div className={classes['event-item-body__info-row']}>
            <IconLabel iconId={'event'} iconSize={16} nonClickable singleColor />
            {ctxT('registration-closes')}
            <strong>{eventRegistrationDeadline(event)}</strong>
          </div>
        </div>

        <EventParticipants event={event} onSelectTab={onSelectTab} />

        <div className={classes['event-item-body__actions']}>
          {showViewLink && (
            <button
              type={'button'}
              className={classes['event-item-body__view-link']}
              onClick={openEventDetails}
            >
              {ctxT('view')}
            </button>
          )}

          <EventShare className={classes['event-item-body__share']} eventId={eventId} />

          {scheduledAt && (
            <ScheduleIcon
              time={scheduledAt}
              hintDirection={IconLabelHintDirection.bottom}
              nonClickable
            />
          )}

          {allowToEdit && !isCancelled && (
            <PublishToggle
              isPublished={isPublished}
              onClick={onPublishToggle}
              disabled={isPublishLoading}
            />
          )}

          {isEditable && (
            <IconLabel
              iconId={'edit'}
              iconSize={20}
              ariaLabel={t('common.edit')}
              color={getCssVar('--action-pane-icon-color')}
              hoverColor={getCssVar('--action-pane-icon-hover-color')}
              onClick={eventEditHandler}
            />
          )}

          <EventActionButton
            event={event}
            className={classNames({
              [classes['event-item-body__action-button--narrow']]: isNarrowViewport,
            })}
          />

          <EventActionsMenu
            event={event}
            onEdit={eventEditHandler}
            onDeleteSuccess={onDeleteSuccess}
          />
        </div>

        {eventEditModal}
      </>
    );
  }
);
